import { DSL_GRAMMAR, type DslArgumentDefinition } from './dsl-grammar.js';
import { knownFunctionNames } from './expression/known-functions.js';
import {
  type ExampleMembers,
  type ExampleValue,
  objectOf,
  textOf,
  typeOfExample,
} from './expression/parser-detailed.js';
import { lex as lexExpression } from '@crown/dsl-compiler';
import type { ParseExpressionOptions } from './expression.js';

/** What to show when someone rests the pointer on part of an expression: the range covered, and the explanation. */
export interface HoverInfo {
  range: { start: number; end: number };
  text: string;
  type?: string;
  markdown?: string;
  fieldInfo?: { name: string; kind?: string };
}
/** One suggestion offered at a cursor position. */
export interface CompletionItem {
  label: string;
  type?: string;
  detail?: string;
  documentation?: string;
  insertText?: string;
  kind?: 'field' | 'function' | 'keyword' | 'value';
}

/** One declared evaluation-context variable. */
export type CtxVariableDef = {
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description?: string;
  properties?: Record<string, CtxVariableDef>;
  element?: CtxVariableDef;
};

/** Extended options accepted by editor helper functions */
export interface EditorOptions extends ParseExpressionOptions {
  /** Context variable definitions from the schema's top-level `ctx` object */
  contextSchema?: Record<string, CtxVariableDef>;
}

/** One DSL keyword, with the description the editor shows beside it. */
export interface DslKeyword {
  label: string;
  detail: string;
  insertText: string;
  category: 'field' | 'object' | 'dates';
  type?: string;
  description?: string;
  args: readonly DslArgumentDefinition[];
  allowOptions: boolean;
}

const DSL_KEYWORDS: readonly DslKeyword[] = [
  ...DSL_GRAMMAR.fieldOperations.map((op) => ({
    label: `field().${op.name}`,
    detail: op.detail,
    insertText: op.insertText,
    category: op.category,
    type: 'function',
    description: op.description,
    args: op.args,
    allowOptions: op.allowOptions ?? true,
  })),
  ...DSL_GRAMMAR.objectOperations.map((op) => ({
    label: `object.${op.name}`,
    detail: op.detail,
    insertText: op.insertText,
    category: op.category,
    type: 'function',
    description: op.description,
    args: op.args,
    allowOptions: op.allowOptions ?? true,
  })),
  ...DSL_GRAMMAR.dateOperations.map((op) => ({
    label: `dates().${op.name}`,
    detail: op.detail,
    insertText: op.insertText,
    category: op.category,
    type: 'function',
    description: op.description,
    args: op.args,
    allowOptions: op.allowOptions ?? true,
  })),
];

/** Every DSL keyword. Returns a copy, so a caller sorting or filtering cannot disturb the shared list. */
export function getDslKeywords(): DslKeyword[] {
  return [...DSL_KEYWORDS];
}

/** Returns completion items for all built-in expression functions */
export function getBuiltinFunctionCompletions(): CompletionItem[] {
  return [...knownFunctionNames()].map((name) => ({
    label: name,
    type: 'function',
    detail: `${name}(...)`,
    documentation: `Built-in expression function. Use as \`${name}(args)\` in expressions.`,
    kind: 'function' as const,
  }));
}

/** Returns hover info for a built-in function by name, or undefined if not found */
export function getBuiltinFunctionHover(name: string): HoverInfo | undefined {
  if (!knownFunctionNames().has(name)) return undefined;
  return {
    range: { start: 0, end: name.length },
    text: name,
    type: 'function',
    markdown: `**${name}** \`(built-in)\`\n\nBuilt-in expression function. Use as \`${name}(args)\`.`,
  };
}

/** Resolve a dot-separated path into a contextSchema tree, following both flat keys and .properties nesting */
function resolveCtxPath(
  contextSchema: Record<string, CtxVariableDef>,
  parts: string[],
): Record<string, CtxVariableDef> | null {
  let cur: Record<string, CtxVariableDef> | undefined = contextSchema;
  for (const part of parts) {
    if (!part || !cur) return null;
    const node: CtxVariableDef | undefined = cur[part];
    if (!node) return null;
    // descend into .properties for object nodes
    cur = node.properties ?? undefined;
  }
  return cur ?? null;
}

/**
 * Explain whatever sits at a position in an expression.
 *
 * Returns `undefined` when there is nothing useful to say, rather than an empty
 * result - an editor should show no tooltip at all rather than an empty one.
 */
export function getHoverInfo(
  source: string,
  position: number,
  options: EditorOptions = {},
): HoverInfo | undefined {
  const lineStart = source.lastIndexOf('\n', position) + 1;
  const lineContent = source.slice(
    lineStart,
    source.indexOf('\n', position) >= 0
      ? source.indexOf('\n', position)
      : undefined,
  );
  const commentStart = lineContent.indexOf('#');
  if (commentStart >= 0 && position >= lineStart + commentStart) {
    return undefined;
  }

  // Check for ctx.* hover before full parse
  const upTo = source.slice(0, position);
  const ctxPathMatch = /ctx\.((?:[A-Za-z0-9_]+\.)*)([A-Za-z0-9_]*)$/.exec(upTo);
  if (ctxPathMatch && options.contextSchema) {
    const intermediateParts = (ctxPathMatch[1] || '')
      .split('.')
      .filter(Boolean);
    const lastName = ctxPathMatch[2] || '';
    // resolve down the path
    let cur: Record<string, CtxVariableDef> | null = options.contextSchema;
    for (const part of intermediateParts) {
      if (!cur) break;
      const node: CtxVariableDef | undefined = cur[part];
      cur = node?.properties ?? null;
    }
    const def = cur?.[lastName];
    if (def) {
      const fullPath = `ctx.${[...intermediateParts, lastName].join('.')}`;
      return {
        range: { start: position - lastName.length, end: position },
        text: lastName,
        type: 'ctx-field',
        markdown: `**${fullPath}** \`(${def.type})\`\n\n${def.description || 'Context variable'}`,
        fieldInfo: { name: fullPath, kind: def.type },
      };
    }
    if (upTo.endsWith('ctx')) {
      return {
        range: { start: position - 3, end: position },
        text: 'ctx',
        type: 'context',
        markdown:
          '**ctx**: External context object. Access runtime variables like `ctx.teamRegion`, `ctx.user.role`, etc.',
      };
    }
  }

  /*
    Hover needs the token under the pointer, and nothing else about the parse.

    It used to take that from `parseExpressionDetailed`, which parses the whole
    source and infers its types to answer a question about one token. Worse,
    it lexed with a second lexer: hovering a comment found no token at all,
    because that lexer and the engine's disagreed about whether a comment is
    one.
  */
  const parsed = lexExpression(source);
  if (!parsed.tokens.length) return undefined;
  const token = parsed.tokens.find(
    (t) => position >= t.index && position < t.index + t.length,
  );
  if (!token) return undefined;
  const text = token.value;
  let type: string | undefined;
  let markdown: string | undefined;
  let fieldInfo: { name: string; kind?: string } | undefined;

  if (token.type === 'ident') {
    if (text === 'value') {
      type = 'root';
      markdown = '**value**: Current field value';
    } else if (text === 'root') {
      type = 'root';
      markdown =
        '**root**: Root form data object - access fields via `root.fieldName`';
    } else if (text === 'ctx') {
      type = 'context';
      markdown =
        '**ctx**: External context object. Access runtime variables like `ctx.teamRegion`, `ctx.user.role`, etc.';
    } else if (
      options.documentSchema &&
      source.slice(0, token.index).trimEnd().endsWith('root.')
    ) {
      type = 'field';

      const prefix = source.slice(0, token.index);
      const pathMatch = /root\.([A-Za-z0-9_.]+)$/.exec(prefix + text);

      let fieldDef: ExampleValue | undefined;
      if (pathMatch) {
        const path = (pathMatch[1] ?? '').split('.');
        let current: ExampleValue | undefined = options.documentSchema;
        for (let pi = 0; pi < path.length; pi++) {
          const seg = path[pi];
          const node = objectOf(current);
          if (!node || seg === undefined) {
            current = undefined;
            break;
          }
          if (seg in node) {
            current = node[seg];
            // Descend into .properties for intermediate MetaObjectField segments
            const next = objectOf(current);
            if (
              pi < path.length - 1 &&
              textOf(next?.kind) === 'object' &&
              next?.properties
            ) {
              current = next.properties;
            }
          } else {
            current = undefined;
            break;
          }
        }
        fieldDef = current;
      }

      if (fieldDef) {
        const node = objectOf(fieldDef);
        const kind = textOf(node?.kind) || typeOfExample(fieldDef);
        fieldInfo = { name: text, kind };
        markdown = `**${text}** \`(${kind})\`\n\n${textOf(node?.title) || textOf(node?.description) || 'Field in schema'}`;
      } else {
        markdown = `**${text}** (field)`;
      }
    } else if (knownFunctionNames().has(text)) {
      // Built-in function
      const hover = getBuiltinFunctionHover(text);
      if (hover)
        return {
          ...hover,
          range: { start: token.index, end: token.index + token.length },
        };
    }
  } else if (token.type === 'op') {
    const keyword = DSL_KEYWORDS.find((k) => k.label.includes(text));
    if (keyword) {
      markdown = `**${text}** \`${keyword.category}\`\n\n${keyword.description || keyword.detail}`;
      if (keyword.args.length > 0) {
        markdown += '\n\n**Arguments:**\n';
        for (const arg of keyword.args) {
          markdown += `- \`${arg.name}\` (${arg.type}): ${arg.description}\n`;
        }
      }
    }
  }

  return {
    range: { start: token.index, end: token.index + token.length },
    text,
    type,
    markdown,
    fieldInfo,
  };
}

/**
 * What could be typed at a position in an expression.
 *
 * Works on incomplete source, which is the only kind that exists while someone
 * is typing: the text is not required to parse.
 */
export function getCompletions(
  source: string,
  position: number,
  options: EditorOptions = {},
): CompletionItem[] {
  const lineStart = source.lastIndexOf('\n', position) + 1;
  const lineContent = source.slice(lineStart, position);
  const commentStart = lineContent.indexOf('#');
  if (commentStart >= 0) {
    return [];
  }

  const upto = source.slice(0, position);

  // ctx.* completions
  const ctxMatch = /(ctx(?:\.[A-Za-z0-9_]+)*)\.$/i.exec(upto);
  if (ctxMatch && options.contextSchema) {
    const chain = (ctxMatch[1] ?? '').split('.').slice(1); // remove leading 'ctx'
    const resolved = resolveCtxPath(options.contextSchema, chain);
    if (resolved) {
      return Object.entries(resolved).map(([k, def]) => ({
        label: k,
        type: def.type,
        detail: `(${def.type})`,
        documentation: def.description || `Context variable: ${k}`,
        kind: 'field' as const,
      }));
    }
    return [];
  }

  // root.* completions
  const rootMatch =
    /(root(?:\??\.)[A-Za-z0-9_]+(?:\??\.(?:[A-Za-z0-9_]+))*|root)\.?$/i.exec(
      upto,
    );
  if (!rootMatch) {
    // General expression completions: built-in functions + special vars + keywords
    const items: CompletionItem[] = [];

    // Special top-level vars
    const specialVars: Array<[string, string]> = [
      ['root', 'Root form data object. Access fields via root.fieldName'],
      ['value', 'Current field value'],
      [
        'ctx',
        'External context variables (ctx.teamRegion, ctx.user.role, etc.)',
      ],
    ];
    for (const [label, doc] of specialVars) {
      items.push({
        label,
        type: 'variable',
        detail: '(variable)',
        documentation: doc,
        kind: 'field' as const,
      });
    }

    // All built-in functions
    const fnItems = getBuiltinFunctionCompletions();
    items.push(...fnItems);

    return items;
  }

  if (!options.documentSchema) return [];
  const chain = (rootMatch[1] || 'root').split(/\??\./).slice(1); // remove 'root'
  let cur: ExampleMembers | null = objectOf(options.documentSchema);
  for (const seg of chain) {
    if (!seg) continue;
    if (cur && seg in cur) {
      cur = objectOf(cur[seg]);
      // Descend into .properties for MetaObjectField entries
      const kind = textOf(cur?.kind);
      if (kind === 'object' && cur?.properties) {
        cur = objectOf(cur.properties);
      } else if (kind && kind !== 'object') {
        return []; // terminal field (string/number/boolean/array)
      }
    } else {
      return [];
    }
  }
  if (!cur) return [];

  const isAtRoot = chain.filter(Boolean).length === 0;
  const items: CompletionItem[] = [];

  for (const k of Object.keys(cur)) {
    const fieldValue = cur[k];
    const field = objectOf(fieldValue);
    const kind = textOf(field?.kind) || typeOfExample(fieldValue);
    const title = textOf(field?.title) || textOf(field?.description) || '';

    items.push({
      label: k,
      type: kind,
      detail: `(${kind})`,
      documentation: title || `Field: ${k}`,
      kind: 'field' as const,
    });

    // When at root level, also include first-level nested dot paths for object fields
    if (isAtRoot && kind === 'object' && field?.properties) {
      for (const [subK, subValue] of Object.entries(
        objectOf(field.properties) ?? {},
      )) {
        const subField = objectOf(subValue);
        const subKind = textOf(subField?.kind) || 'unknown';
        const subTitle =
          textOf(subField?.title) || textOf(subField?.description) || '';
        items.push({
          label: `${k}.${subK}`,
          type: subKind,
          detail: `(${subKind})`,
          documentation: subTitle || `${k}.${subK}`,
          kind: 'field' as const,
        });
      }
    }
  }

  return items;
}
